import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import { Chart, PieController, ArcElement, Tooltip, Legend, Title, } from "chart.js"
import { normalizeData } from "./visitor-profile-helper"
import { SurveyItemRaw } from "./reports-page-types"

Chart.register(PieController, ArcElement, Tooltip, Legend, Title)

const PIE_COLORS = [
  "#4472C4",
  "#ED7D31",
  "#A5A5A5",
  "#FFC000",
  "#5B9BD5",
  "#70AD47",
  "#264478",
  "#9B59B6",
]
const HEADER_COLOR: [number, number, number] = [255, 94, 30] // #2F5496
const PAGE_MARGIN = 20

function renderPieChartToDataUrl(title: string, labels: string[], values: number[]): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas")
    canvas.width = 700
    canvas.height = 450

    const ctx = canvas.getContext("2d")
    if (!ctx) {
      reject(new Error("Could not get canvas 2d context"))
      return
    }

    ctx.fillStyle = "#FFFFFF"
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    const chart = new Chart(ctx, {
      type: "pie",
      data: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: PIE_COLORS.slice(0, labels.length),
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: false,
        animation: false,
        plugins: {
          title: {
            display: true,
            text: title,
            font: { size: 18, weight: "bold" },
            color: "#FF5E1E",
          },
          legend: {
            position: "right",
            labels: { font: { size: 13 } },
          },
        },
      },
    })

    requestAnimationFrame(() => {
      const dataUrl = canvas.toDataURL("image/png")
      chart.destroy()
      resolve(dataUrl)
    })
  })
}

export async function generateProfileSurveyPdf(rawData: SurveyItemRaw[], fileName: string): Promise<void> {
  const data = normalizeData(rawData)
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" })
  const pageWidth = doc.internal.pageSize.getWidth()

  for (let i = 0; i < data.length; i++) {
    const item = data[i]

    if (i > 0) {
      doc.addPage()
    }

    let cursorY = PAGE_MARGIN

    doc.setFont("helvetica", "bold")
    doc.setFontSize(16)
    doc.setTextColor(...HEADER_COLOR)
    doc.text(item.question, PAGE_MARGIN, cursorY)
    cursorY += 4

    doc.setFont("helvetica", "normal")
    doc.setFontSize(11)
    doc.setTextColor(100, 100, 100)
    doc.text(`Total de respostas: ${item.total}`, PAGE_MARGIN, cursorY + 6)
    cursorY += 14

    const answerKeys = Object.keys(item.answers)
    const tableHeaders = ["Resposta", "Contagem", "Percentual"]
    const tableRows = answerKeys.map((key) => {
      const count = item.answers[key]
      const pct = item.total > 0 ? ((count / item.total) * 100).toFixed(1) : "0.0"
      return [key, String(count), `${pct}%`]
    })

    autoTable(doc, {
      startY: cursorY,
      head: [tableHeaders],
      body: tableRows,
      margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
      theme: "grid",
      headStyles: {
        fillColor: HEADER_COLOR,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 10,
        halign: "center",
      },
      bodyStyles: {
        fontSize: 10,
        halign: "center",
      },
      columnStyles: {
        0: { halign: "left", cellWidth: 70 },
        1: { cellWidth: 30 },
        2: { cellWidth: 30 },
      },
      styles: {
        cellPadding: 3,
        lineColor: [200, 200, 200],
        lineWidth: 0.3,
      },
    })

    const tableEndY = (doc as any).lastAutoTable?.finalY ?? cursorY + 50
    cursorY = tableEndY + 10

    const filtered = Object.entries(item.answers).filter(([, v]) => v > 0)
    if (filtered.length > 0) {
      const labels = filtered.map(([k]) => k)
      const values = filtered.map(([, v]) => v)
      const chartDataUrl = await renderPieChartToDataUrl(
        item.question,
        labels,
        values
      )

      const availableWidth = pageWidth - PAGE_MARGIN * 2
      const chartWidth = Math.min(availableWidth, 160)
      const chartHeight = chartWidth * (450 / 700)
      const chartX = (pageWidth - chartWidth) / 2

      doc.addImage(chartDataUrl, "PNG", chartX, cursorY, chartWidth, chartHeight)
    }

    const pageHeight = doc.internal.pageSize.getHeight()
    doc.setFont("helvetica", "normal")
    doc.setFontSize(9)
    doc.setTextColor(150, 150, 150)
    doc.text(
      `Página ${i + 1} de ${data.length}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: "center" }
    )
  }

  doc.save(`${fileName}.pdf`)
}
